import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SaveProductButton from './SaveProductButton';
import AddToBasketButton from './AddToBasketButton';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL, getHeaders } from '../config/api';
import slugify from '../utils/slugify';

export default function PopularProducts() {
  const [popularProducts, setPopularProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { authToken } = useAuth();

  useEffect(() => {
    const fetchPopularProducts = async () => {
      setLoading(true);
      setError('');
      try {
        const headers = getHeaders(authToken);
        const response = await fetch(`${API_BASE_URL}/products/popular?limit=5`, { headers });
        const data = await response.json();
        if (response.ok) {
          // The API directly returns an array of products or an object with a data key
          setPopularProducts(Array.isArray(data) ? data : data.data || []);
        } else {
          setError(data.message || 'Failed to fetch popular products.');
        }
      } catch (err) {
        console.error('Fetch popular products error:', err);
        setError('An error occurred while fetching popular products.');
      }
      setLoading(false);
    };

    fetchPopularProducts();
  }, [authToken]);

  const handleProductSaveStatusChange = (productId, newSavedStatus) => {
    setPopularProducts(prevProducts => 
      prevProducts.map(product => 
        product.id === productId ? { ...product, is_saved_by_user: newSavedStatus } : product
      )
    );
  };

  if (loading) {
    return (
      <section className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold mb-8 text-gray-800 text-center">Popular Products</h2>
        <div className="text-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-800 mx-auto"></div>
          <p className="text-gray-500 mt-3">Loading popular products...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold mb-8 text-gray-800 text-center">Popular Products</h2>
        <div className="text-center py-10 bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg shadow-sm max-w-md mx-auto">
          <h3 className="font-semibold text-md mb-1">Could not load popular products.</h3>
          <p className="text-sm">Error: {error}</p>
        </div>
      </section>
    );
  }

  if (popularProducts.length === 0) {
    return (
      <section className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold mb-8 text-gray-800 text-center">Popular Products</h2>
        <div className="text-center py-10 bg-gray-50 border border-gray-200 p-6 rounded-lg shadow-sm max-w-md mx-auto">
           <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <h3 className="mt-2 text-md font-medium text-gray-800">No Popular Products</h3>
          <p className="mt-1 text-sm text-gray-500">Check back later for popular product updates.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-6xl mx-auto px-4 py-12">
      <h2 className="text-3xl font-bold mb-8 text-gray-800 text-center">Popular Products</h2>
      {/* Horizontal scroll on mobile, grid on larger screens */}
      <div className="flex gap-4 overflow-x-auto pb-4 md:grid md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 md:gap-6 md:overflow-visible">
        {popularProducts.map((product) => (
          <div key={product.id} className="flex-shrink-0 w-64 md:w-auto bg-white rounded-xl shadow-md hover:shadow-lg transition duration-200 ease-in-out transform hover:-translate-y-1 flex flex-col p-4">
            <Link 
              to={`/product/${product.id}`} 
              className="flex flex-col items-center flex-grow w-full"
            >
              <img 
                src={product.image_url || product.img || '/placeholder-product.svg'}
                alt={product.name}
                className="w-32 h-32 object-cover rounded-lg mb-4 shadow"
              />
              <h3 className="font-semibold text-gray-800 text-center mb-1 h-12 overflow-hidden">{product.name}</h3>
              {product.price && (
                <div className="text-red-800 font-bold text-lg mb-2">${parseFloat(product.price).toFixed(2)}</div>
              )}
            </Link>
            <div className="w-full mt-2">
                <Link 
                    to={`/product/${product.id}`} 
                    className="block w-full text-center bg-red-800 text-white px-3 py-2 rounded-md hover:bg-red-900 transition text-sm mb-1.5"
                >
                    View Details
                </Link>
                <SaveProductButton 
                    productId={product.id} 
                    initialIsSaved={product.is_saved_by_user || false} 
                    onSaveStatusChange={handleProductSaveStatusChange} 
                />
                <AddToBasketButton productId={product.id} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
} 